import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Hidden } from '@material-ui/core';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { TcsmAppConfigs } from '../../../../../global/models/configuration-models';
import { DemographicAppKeys, DemographicsContext } from '../../../../../global/models/demographics-model';
import { useBasePath } from '../../../../../global/hooks/useBasePath';
import { AccountInfoRoutes } from '../../../../account-info/constants/routes';
import { ArcRoutes } from '../../../constants/routes';
import { useNewHireSettings } from '../../new-hire/hooks/useNewHireSettings';
import { TcsmStates } from '../../../../../root/tcsm-model';
import { GuidedProcessSupportProps, NewHireProps } from '../../../models/arc-epi-props';
import { setCurrentNewHireEnrollmentStep } from '../slice/new-hire-enrollment-slices';
import { NewHireEnrollmentStep } from '../models/new-hire-enrollment-models';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { FormButtons } from '../../../../../global/components/form-element/form-buttons';
import { ButtonWrapper } from '../../../../../global/components/layout/button-wrapper';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { DividerSection } from '../../../../../../stories/divider-section';
import { NewHireEnrollmentRoutes } from '../models/new-hire-routes';
import { buttonStyles } from '../../../../../global/constants/theme/styles/components/buttonstyles';
import { useMyCpgMidYearOpenEnrollments } from '../../../../../global/hooks/useMyCpgMidYearOpenEnrollments';
import { MyCPGMidYearProps } from '../../../../account-info/features/mycpg-midyear/models/mycpg-midyear-epi-props';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { useGpsType } from '../../../../../global/hooks/useGpsType';


interface Props {
    appSettings: TcsmAppConfigs;
    basePath: string;
    isMidYear?: boolean;
}

export function MemberNewHireWelcomePage(props: Props) {

    const buttonClasses = buttonStyles();

    const { appSettings, basePath, isMidYear } = props;
    const history = useHistory();
    const dispatch = useDispatch();
    const openMidYearEnrollments = useMyCpgMidYearOpenEnrollments();
    const app = useContext(DemographicsContext);
	const isArc = app === DemographicAppKeys.arc;

    const myCPGRootPath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}`);
    const arcRootPath = useBasePath(`${ArcRoutes.ArcRoot}`);

    const settingsLoaded = useNewHireSettings(appSettings.newHireSettingsId);
    const newHireSettings = useSelector<TcsmStates, NewHireProps>(state => state.epiProps?.newHireSettingsState?.epiContent);
    const midYearSettings = useSelector<TcsmStates, MyCPGMidYearProps>(state => state?.epiProps?.myCpgMidyearSettingsState?.epiContent);
    
    const isEligible = useSelector<TcsmStates, boolean>(state => state?.newHireEnrollmentState?.isEligible);

    const gpsSettings = useSelector<TcsmStates, GuidedProcessSupportProps>(state => state.epiProps?.gpsSettingsState?.epiContent);
    const MidYearGpsItem = useGpsType(GpsType.MID_YEAR_WELCOME_PAGE);
    const gpsTitle = isMidYear ? MidYearGpsItem?.title : gpsSettings?.memberNewHireEnrollmentLandingSectionTitle;
    const gpsItem =  isMidYear ? MidYearGpsItem?.contents : gpsSettings?.memberNewHireEnrollmentLandingSectionArea;
    const haveOpenMidYearEnrollments = openMidYearEnrollments?.length > 0;

    useEffect(() => {
		dispatch(setCurrentNewHireEnrollmentStep(NewHireEnrollmentStep.EMPLOYEE_RECORD));
	}, []);

    function onCancel() {
        history.push(isArc ? arcRootPath : myCPGRootPath);
    }

    function onEnroll() {
        dispatch(setCurrentNewHireEnrollmentStep(NewHireEnrollmentStep.EMPLOYEE_RECORD));
        history.push(`${basePath}${NewHireEnrollmentRoutes.EmployeeRecord}${AccountInfoRoutes.PersonalInfoSegment}`);
    }

    function RenderWelcomeMessage() {

        if (isEligible) {
            return (
                <>
                    <ContentSpacer>
                        <WysiwygWithReactLink content={newHireSettings?.memberWelcomePageDescription} />
                    </ContentSpacer>
                    <div>
                        <FormButtons
                            disabled={false}
                            submitText={newHireSettings?.memberWelcomePageStartEnrollmentButtonText || 'Enroll'}
                            cancelText={newHireSettings?.memberWelcomePageGotoDashboardButtonText || 'Go to Dashboard'}
                            onSubmit={onEnroll}
                            onCancel={onCancel}
                        />
                    </div>
                </>
            );

        }

        return (
            <>
                <ContentSpacer>
                    <WysiwygWithReactLink content={newHireSettings?.welcomePageDescriptionOutsideOfEligibility} />
                </ContentSpacer>
                <div>
                    <ButtonWrapper flexEnd>
                        <button type='button' className={buttonClasses.tertiaryButton} onClick={onCancel}>{newHireSettings?.memberWelcomePageGotoDashboardButtonText || 'Go to Dashboard'}</button>
                    </ButtonWrapper>
                </div>
            </>
        );
    }

    function RenderMidYearWelcomeMessage() {

        if (haveOpenMidYearEnrollments) {
            return (
                <>
                    <ContentSpacer>
                        <WysiwygWithReactLink content={midYearSettings?.midYearWelcomePageDescriptions} />
                    </ContentSpacer>
                    <div>
                        <FormButtons
                            disabled={false}
                            submitText={midYearSettings?.midYearWelcomePageStartEnrollmentButtonText || 'Enroll'}
                            cancelText={midYearSettings?.midYearWelcomePageGotoDashboardButtonText || 'Go to Dashboard'}
                            onSubmit={onEnroll}
                            onCancel={onCancel}
                        />
                    </div>
                </>
            );

        }

        return (
            <>
                <ContentSpacer>
                    <WysiwygWithReactLink content={midYearSettings?.welcomePageDescriptionOutsideOfEligibility} />
                </ContentSpacer>
                <div>
                    <ButtonWrapper flexEnd>
                        <button type='button' className={buttonClasses.tertiaryButton} onClick={onCancel}>{midYearSettings?.midYearWelcomePageGotoDashboardButtonText || 'Go to Dashboard'}</button>
                    </ButtonWrapper>
                </div>
            </>
        );
    }

    if (!settingsLoaded) {
        return <LoadingCircle />;
    }

    return (
        <>
            <PageContainer >
                <MainContent>
                    <PageHeader
                        headerText={ isMidYear ? midYearSettings.midYearWelcomePageTitle : newHireSettings.memberWelcomePageTitle}
                    />
                    <Hidden mdUp>
                        <SideBarContent>
                            <GuidedProcessSupportBlock title={gpsTitle} contents={gpsItem} />
                        </SideBarContent>
                    </Hidden>
                    <DividerSection removeDivider>
                        {
                            isMidYear ? 
                            <RenderMidYearWelcomeMessage /> :
                            <RenderWelcomeMessage />

                        }
                    </DividerSection>

                </MainContent>
                <Hidden smDown>
                    <SideBarContent>
                        <GuidedProcessSupportBlock title={gpsTitle} contents={gpsItem} />
                    </SideBarContent>
                </Hidden>
            </PageContainer>
        </>
    );
}


MemberNewHireWelcomePage.defaultProps = {
    isMidYear: false,
};